function AdminCustomProfileCropperDialog(props) {
  const minZoom = 1;
  const maxZoom = 3;
  const initialZoomHeadroom = 0.12;
  const EasyCrop =
    window.ReactEasyCrop &&
    (window.ReactEasyCrop.default || window.ReactEasyCrop);
  const previewSize =
    window.innerWidth > 767
      ? 300
      : Math.max(Math.min(window.innerWidth - 88, 300), 240);
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoomLevel, setZoomLevel] = React.useState(minZoom);
  const [resolvedMinZoom, setResolvedMinZoom] = React.useState(minZoom);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const hasAppliedInitialZoomRef = React.useRef(false);

  React.useEffect(
    function () {
      if (!props.show) {
        return;
      }

      hasAppliedInitialZoomRef.current = false;
      setCrop({ x: 0, y: 0 });
      setResolvedMinZoom(minZoom);
      setZoomLevel(minZoom);
      setCroppedAreaPixels(null);
      setIsSubmitting(false);
    },
    [props.show, props.imageSrc],
  );

  function handleMediaLoaded(mediaSize) {
    const width = mediaSize.naturalWidth || mediaSize.width || 1;
    const height = mediaSize.naturalHeight || mediaSize.height || 1;
    const coverZoom = Math.max(width / height, height / width);
    const nextMinZoom = Math.min(Math.max(coverZoom, minZoom), maxZoom);
    const nextInitialZoom = Math.min(nextMinZoom + initialZoomHeadroom, maxZoom);

    setResolvedMinZoom(nextMinZoom);

    if (!hasAppliedInitialZoomRef.current) {
      hasAppliedInitialZoomRef.current = true;
      setZoomLevel(nextInitialZoom);
      return;
    }

    setZoomLevel(function (currentZoom) {
      return currentZoom < nextMinZoom ? nextMinZoom : currentZoom;
    });
  }

  function handleCropComplete(_, nextCroppedAreaPixels) {
    setCroppedAreaPixels(nextCroppedAreaPixels);
  }

  function handleZoomChange(event) {
    setZoomLevel(parseFloat(event.target.value) || resolvedMinZoom);
  }

  function handleConfirm() {
    if (!props.imageSrc || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    getAdminCustomProfileCroppedImage(props.imageSrc, croppedAreaPixels)
      .then(function (croppedImage) {
        return props.onConfirm(croppedImage);
      })
      .catch(function () {
        if (props.toast) {
          props.toast.error(
            (props.lang && props.lang.__setting_admin_custom_profile_upload_unsuccessfully) ||
              "Failed to process image."
          );
        }
      })
      .then(function () {
        setIsSubmitting(false);
      });
  }

  return (
    <div>
      <div
        className={`modal admin-custom-profile-cropper-modal ${
          props.show ? "fade in" : ""
        }`}
        tabIndex="-1"
        role="dialog"
        style={{ display: props.show ? "block" : "none" }}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">
                {props.lang.__setting_admin_custom_profile_upload_image}
              </h3>
            </div>
            <div className="modal-body admin-custom-profile-cropper-modal-body">
              <div className="admin-custom-profile-cropper-preview">
                {EasyCrop ? (
                  <EasyCrop
                    image={props.imageSrc}
                    crop={crop}
                    zoom={zoomLevel}
                    aspect={1}
                    cropShape="round"
                    objectFit="cover"
                    showGrid={false}
                    cropSize={{ width: previewSize, height: previewSize }}
                    minZoom={resolvedMinZoom}
                    maxZoom={maxZoom}
                    restrictPosition={true}
                    zoomWithScroll={false}
                    onCropChange={setCrop}
                    onCropComplete={handleCropComplete}
                    onCropAreaChange={handleCropComplete}
                    onMediaLoaded={handleMediaLoaded}
                    onZoomChange={setZoomLevel}
                    classes={{
                      containerClassName:
                        "admin-custom-profile-cropper-easy-container",
                      cropAreaClassName:
                        "admin-custom-profile-cropper-easy-area",
                      mediaClassName: "admin-custom-profile-cropper-easy-media",
                    }}
                  />
                ) : null}
              </div>
              <div className="admin-custom-profile-cropper-controls">
                <input
                  type="range"
                  className="admin-custom-profile-cropper-slider"
                  min={String(resolvedMinZoom)}
                  max={String(maxZoom)}
                  step="0.05"
                  value={zoomLevel}
                  onChange={handleZoomChange}
                  aria-label="Zoom image"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="sp-btn admin-custom-profile-cropper-btn -cancel"
                type="button"
                onClick={props.onCancel}
                disabled={isSubmitting}
              >
                {props.lang.__cancel}
              </button>
              <button
                className="sp-btn admin-custom-profile-cropper-btn -confirm"
                type="button"
                onClick={handleConfirm}
                disabled={!EasyCrop || isSubmitting}
              >
                {props.lang.__upload}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`modal-backdrop ${props.show ? "fade in" : ""}`}
        style={{ display: props.show ? "" : "none" }}
      ></div>
    </div>
  );
}
